<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import OnboardingAPI from 'dashboard/api/onboarding';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  progress: { type: Object, required: true },
  // draftData is unused here — declared only to satisfy the wizard's uniform
  // step-component contract (props: { progress, draftData }) shared by every
  // STEP_COMPONENTS entry; step 11 is a self-contained polling/summary screen.
  // eslint-disable-next-line vue/no-unused-properties
  draftData: { type: Object, default: null },
});

const { t } = useI18n();
const router = useRouter();

// Same order of magnitude as the Gupshup connection polling
// (dashboard/routes/dashboard/settings/inbox/GupshupOnboarding.vue), tuned down
// slightly since these jobs are typically faster than an external OAuth flow.
const POLLING_INTERVAL_MS = 4000;
const MAX_POLLING_ATTEMPTS = 60;

const tasks = ref([]);
const retryingTaskIds = ref(new Set());
let pollingIntervalId = null;

const allResolved = computed(
  () =>
    tasks.value.length > 0 &&
    tasks.value.every(task => ['completed', 'failed'].includes(task.status))
);

const allCompleted = computed(
  () =>
    tasks.value.length > 0 &&
    tasks.value.every(task => task.status === 'completed')
);

// The wizard only ever advances the account to in_progress; this screen is what finally
// marks it completed — but only once every applied item actually succeeded (no failures),
// so the resume sidebar item / agent modal / Super Admin status all reflect real completion.
// Failed items leave the account in_progress so the admin can retry later.
let onboardingMarkedCompleted = false;
const markOnboardingCompletedIfDone = async () => {
  if (onboardingMarkedCompleted || !allCompleted.value) return;
  onboardingMarkedCompleted = true;
  try {
    await OnboardingAPI.update({ status: 'completed' });
  } catch (error) {
    onboardingMarkedCompleted = false;
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

const stopPolling = () => {
  if (pollingIntervalId) {
    clearInterval(pollingIntervalId);
    pollingIntervalId = null;
  }
};

const fetchTasks = async () => {
  try {
    const { data } = await OnboardingAPI.getCompletionTasks();
    tasks.value = data;
    if (allResolved.value) stopPolling();
    await markOnboardingCompletedIfDone();
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

const startPolling = () => {
  stopPolling();
  let attempts = 0;

  pollingIntervalId = setInterval(async () => {
    attempts += 1;
    if (attempts > MAX_POLLING_ATTEMPTS) {
      stopPolling();
      return;
    }
    await fetchTasks();
  }, POLLING_INTERVAL_MS);
};

const retryTask = async task => {
  retryingTaskIds.value.add(task.id);
  try {
    const { data } = await OnboardingAPI.retryCompletionTask(task.id);
    tasks.value = tasks.value.map(item => (item.id === data.id ? data : item));
    if (allResolved.value) {
      await markOnboardingCompletedIfDone();
    } else {
      startPolling();
    }
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  } finally {
    retryingTaskIds.value.delete(task.id);
  }
};

const goToPlatform = () => {
  router.push({ name: 'home' });
};

// Most of the wizard's own steps apply directly to real resources as the admin
// fills them in (see Step02Agents/Step04Distribution/etc.) — there's nothing left
// to poll for those, so their line in the summary just reflects whether the step
// itself was ever completed vs. deferred. The one exception is the WhatsApp
// template, which goes through Onboarding::SubmitTemplateJob and can genuinely
// still be pending/processing/failed here — that's the only line backed by `tasks`.
const isStepDone = step => props.progress.completed_steps.includes(step);

const templateTask = computed(() =>
  tasks.value.find(task => task.task_type === 'template')
);

// One line per wizard step (1-10) — a full recap of everything passed
// through, not just a curated few, so every step's final state is visible here.
const summaryItems = computed(() => [
  {
    key: 'inbox',
    label: t('ONBOARDING.STEP_11.SUMMARY.INBOX'),
    done: isStepDone(1),
  },
  {
    key: 'attendants',
    label: t('ONBOARDING.STEP_11.SUMMARY.ATTENDANTS'),
    done: isStepDone(2),
  },
  {
    key: 'teams',
    label: t('ONBOARDING.STEP_11.SUMMARY.TEAMS'),
    done: isStepDone(3),
  },
  {
    key: 'distribution',
    label: t('ONBOARDING.STEP_11.SUMMARY.DISTRIBUTION'),
    done: isStepDone(4),
  },
  {
    key: 'working_hours',
    label: t('ONBOARDING.STEP_11.SUMMARY.WORKING_HOURS'),
    done: isStepDone(5),
  },
  {
    key: 'ai_agent',
    label: t('ONBOARDING.STEP_11.SUMMARY.AI_AGENT'),
    done: isStepDone(6),
  },
  {
    key: 'canned_responses',
    label: t('ONBOARDING.STEP_11.SUMMARY.CANNED_RESPONSES'),
    done: isStepDone(7),
  },
  {
    key: 'template',
    label: t('ONBOARDING.STEP_11.SUMMARY.TEMPLATE'),
    task: templateTask.value,
  },
  {
    key: 'csat',
    label: t('ONBOARDING.STEP_11.SUMMARY.CSAT'),
    done: isStepDone(9),
  },
  {
    key: 'labels',
    label: t('ONBOARDING.STEP_11.SUMMARY.LABELS'),
    done: isStepDone(10),
  },
]);

onMounted(async () => {
  try {
    await OnboardingAPI.complete();
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }

  await fetchTasks();
  if (!allResolved.value) startPolling();
});

onBeforeUnmount(stopPolling);

defineExpose({ tasks, fetchTasks, retryTask });
</script>

<template>
  <div
    class="flex flex-col items-center justify-center w-full h-full gap-6 p-8"
  >
    <div
      class="flex items-center justify-center w-16 h-16 rounded-full bg-n-brand/10"
    >
      <i class="text-3xl i-lucide-circle-check-big text-n-brand" />
    </div>
    <div class="flex flex-col items-center max-w-md gap-2 text-center">
      <h1 class="text-2xl font-semibold text-n-slate-12">
        {{ t('ONBOARDING.STEP_11.TITLE') }}
      </h1>
      <p class="text-n-slate-11">{{ t('ONBOARDING.STEP_11.DESCRIPTION') }}</p>
    </div>

    <ul class="flex flex-col w-full max-w-lg gap-3 p-4 rounded-lg bg-n-brand/5">
      <li
        v-for="item in summaryItems"
        :key="item.key"
        class="flex flex-col gap-2"
      >
        <div class="flex items-center gap-2">
          <template v-if="item.task">
            <Spinner
              v-if="['pending', 'processing'].includes(item.task.status)"
              :size="16"
            />
            <i
              v-else-if="item.task.status === 'completed'"
              class="text-base shrink-0 i-lucide-circle-check-big text-n-brand"
            />
            <i
              v-else
              class="text-base shrink-0 i-lucide-circle-x text-n-ruby-9"
            />
          </template>
          <template v-else>
            <i
              v-if="item.done"
              class="text-base shrink-0 i-lucide-circle-check-big text-n-brand"
            />
            <span
              v-else
              class="w-4 h-4 border-2 rounded-full shrink-0 border-n-slate-6"
            />
          </template>
          <span class="text-sm text-n-slate-12">{{ item.label }}</span>
        </div>

        <div
          v-if="item.task && item.task.status === 'failed'"
          class="flex items-center justify-between gap-2 pl-6"
        >
          <p class="text-xs text-n-ruby-11">{{ item.task.error_message }}</p>
          <Button
            type="button"
            variant="faded"
            color="ruby"
            :label="t('ONBOARDING.STEP_11.RETRY')"
            :is-loading="retryingTaskIds.has(item.task.id)"
            @click="retryTask(item.task)"
          />
        </div>
      </li>
    </ul>

    <Button
      type="button"
      :label="t('ONBOARDING.GO_TO_PLATFORM')"
      @click="goToPlatform"
    />
  </div>
</template>
